import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Scale, Package, Award, ClipboardCheck,
    FileText, Download, ChevronLeft, ChevronDown, ChevronUp, FolderOpen, Phone, Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

// ─── Folder definitions ──────────────────────────────────────────────────────
const FOLDERS = [
    {
        key: "legal",
        label: "Documentos Legales",
        description: "Contrato de servicio, diplomas del Ministerio de Salud y Agricultura, póliza de responsabilidad civil",
        icon: Scale,
        color: "#2563EB",
        bg: "#EFF6FF",
        border: "#BFDBFE",
        legacy: [],
    },
    {
        key: "products",
        label: "Fichas de Productos",
        description: "Etiquetas, fichas técnicas y hojas de seguridad (MSDS) de los productos utilizados",
        icon: Package,
        color: "#7C3AED",
        bg: "#F5F3FF",
        border: "#DDD6FE",
        legacy: [],
    },
    {
        key: "certifications",
        label: "Contactos y Certificaciones",
        description: "Contactos de Truly Nolen y diplomas de certificación de los técnicos",
        icon: Award,
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FDE68A",
        legacy: ["certificate"],
    },
    {
        key: "constancias",
        label: "Protocolos de Servicio",
        description: "Protocolos y procedimientos del servicio de control de plagas",
        icon: ClipboardCheck,
        color: "#DC2626",
        bg: "#FFF5F5",
        border: "#FECACA",
        legacy: ["other"],
    },
    // reportes_mip is excluded from Documents — it lives in /portal/reports
];

function folderOf(doc) {
    const t = doc.document_type;
    for (const f of FOLDERS) {
        if (f.key === t || f.legacy.includes(t)) return f.key;
    }
    return "constancias";
}

// ─── FolderCard ──────────────────────────────────────────────────────────────
function FolderCard({ folder, count, selected, onClick }) {
    const Icon = folder.icon;
    const isEmpty = count === 0;
    return (
        <button data-testid={`folder-${folder.key}`} onClick={onClick} className="text-left w-full focus:outline-none">
            <div
                className="rounded-xl border-2 transition-all duration-200 overflow-hidden"
                style={{
                    borderColor: selected ? folder.color : (isEmpty ? "#E5E7EB" : folder.border),
                    background: selected ? folder.bg : "#fff",
                    boxShadow: selected ? `0 0 0 3px ${folder.color}22` : "none",
                }}
            >
                <div className="h-2 w-1/2 rounded-br-lg" style={{ background: selected ? folder.color : (isEmpty ? "#E5E7EB" : folder.border) }} />
                <div className="p-4 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-2">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isEmpty ? "#F3F4F6" : folder.bg }}>
                            <Icon className="w-5 h-5" style={{ color: isEmpty ? "#9CA3AF" : folder.color }} />
                        </div>
                        <Badge className="text-xs font-bold shrink-0" style={{ background: isEmpty ? "#F3F4F6" : folder.bg, color: isEmpty ? "#6B7280" : folder.color, border: `1px solid ${isEmpty ? "#E5E7EB" : folder.border}` }}>
                            {count}
                        </Badge>
                    </div>
                    <p className="font-bold text-sm leading-tight text-zinc-900 line-clamp-1">{folder.label}</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5 leading-snug line-clamp-2">{folder.description}</p>
                </div>
            </div>
        </button>
    );
}

// ─── DocumentRow ─────────────────────────────────────────────────────────────
function DocumentRow({ doc, folder, label }) {
    const Icon = folder?.icon || FileText;
    const [downloading, setDownloading] = useState(false);
    const dateLabel = doc.created_at ? format(new Date(doc.created_at), "d 'de' MMMM yyyy", { locale: es }) : "";

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const token = localStorage.getItem("trulyn_token");
            const res = await fetch(`${BASE_URL}/api/documents/${doc.id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`Error ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${doc.title}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            console.error("Download error:", err);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="flex items-center gap-3 p-3 rounded-xl border bg-white hover:bg-zinc-50 transition-colors group" data-testid={`doc-row-${doc.id}`}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: folder?.bg || "#F3F4F6" }}>
                <Icon className="w-4 h-4" style={{ color: folder?.color || "#6B7280" }} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-800 truncate">{label || doc.title}</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">{dateLabel}</p>
            </div>
            <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-xs font-semibold shrink-0"
                style={{ color: folder?.color || "#374151" }}
                onClick={handleDownload}
                disabled={downloading}
                data-testid={`doc-download-${doc.id}`}
            >
                <Download className="w-3.5 h-3.5" />
                {downloading ? "Descargando..." : "Descargar"}
            </Button>
        </div>
    );
}

// ─── Product grouping (Fichas de Productos → accordion per product) ────────────
const PRODUCT_SLOT_LABELS = {
    ficha_tecnica: "Ficha Técnica",
    etiqueta: "Viñeta / Etiqueta",
    msds: "Hoja de Seguridad (MSDS)",
    certificado_libre_venta: "Certificado de Libre Venta",
};
const PRODUCT_SLOT_ORDER = ["ficha_tecnica", "etiqueta", "msds", "certificado_libre_venta"];

function groupProductDocs(docs) {
    const map = {};
    for (const doc of docs) {
        let name = doc.title;
        let slotLabel = doc.title;
        let slotKey = null;
        const dash = doc.title.indexOf(" — ");
        if (dash > -1) {
            name = doc.title.slice(0, dash);
            slotLabel = doc.title.slice(dash + 3);
        }
        if (typeof doc.id === "string" && doc.id.startsWith("product_")) {
            const rest = doc.id.slice("product_".length);
            const us = rest.indexOf("_");
            if (us > -1) slotKey = rest.slice(us + 1);
        }
        if (slotKey && PRODUCT_SLOT_LABELS[slotKey]) slotLabel = PRODUCT_SLOT_LABELS[slotKey];
        if (!map[name]) map[name] = { key: name.replace(/\s+/g, "-").toLowerCase(), name, docs: [] };
        map[name].docs.push({ doc, slotKey, slotLabel });
    }
    const groups = Object.values(map);
    for (const g of groups) {
        g.docs.sort((a, b) => {
            const ai = PRODUCT_SLOT_ORDER.indexOf(a.slotKey);
            const bi = PRODUCT_SLOT_ORDER.indexOf(b.slotKey);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
    }
    groups.sort((a, b) => a.name.localeCompare(b.name));
    return groups;
}

function ProductGroupCard({ product, folder, expanded, onToggle }) {
    return (
        <Card className="overflow-hidden" data-testid={`product-group-${product.key}`}>
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
                data-testid={`product-toggle-${product.key}`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: folder.bg }}>
                        <Package className="w-5 h-5" style={{ color: folder.color }} />
                    </div>
                    <div className="min-w-0">
                        <p className="font-bold text-sm text-zinc-900 truncate">{product.name}</p>
                        <p className="text-[11px] text-zinc-400">
                            {product.docs.length} documento{product.docs.length !== 1 ? "s" : ""}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Badge className="text-xs font-bold" style={{ background: folder.bg, color: folder.color, border: `1px solid ${folder.border}` }}>
                        {product.docs.length}
                    </Badge>
                    {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                </div>
            </button>
            {expanded && (
                <div className="border-t px-3 py-3 space-y-2 bg-zinc-50/50" data-testid={`product-docs-${product.key}`}>
                    {product.docs.map(({ doc, slotLabel }) => (
                        <DocumentRow key={doc.id} doc={doc} folder={folder} label={slotLabel} />
                    ))}
                </div>
            )}
        </Card>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function PortalDocuments() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { selectedBranchId, selectedBranch } = useBranch();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFolder, setActiveFolder] = useState(null);
    const [sheetLoading, setSheetLoading] = useState(false);
    const [expandedProduct, setExpandedProduct] = useState(null);

    const generateContactSheet = async () => {
        setSheetLoading(true);
        try {
            const r = await api.post("/contacts/sheet", selectedBranchId ? { branch_id: selectedBranchId } : {});
            const url = `${BASE_URL}${r.data.pdf_url}`;
            window.open(url, "_blank");
            toast.success("Hoja de contactos generada");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo generar la hoja de contactos");
        } finally {
            setSheetLoading(false);
        }
    };

    // Auto-select folder from URL query param (e.g. ?folder=legal)
    useEffect(() => {
        const folderParam = searchParams.get("folder");
        if (folderParam && FOLDERS.some(f => f.key === folderParam)) {
            setActiveFolder(folderParam);
            setSearchParams({}, { replace: true }); // clean URL
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        setLoading(true);
        const params = selectedBranchId ? { branch_id: selectedBranchId } : {};
        api.get("/documents/portal-smart", { params })
            .then((r) => setDocs(r.data || []))
            .catch(() => {
                // Fallback to regular endpoint
                api.get("/documents", { params }).then((r) => setDocs(r.data || []));
            })
            .finally(() => setLoading(false));
    }, [selectedBranchId]);

    const byFolder = {};
    for (const f of FOLDERS) byFolder[f.key] = [];
    for (const doc of docs) {
        const fk = folderOf(doc);
        if (byFolder[fk]) byFolder[fk].push(doc);
    }

    const currentFolder = FOLDERS.find((f) => f.key === activeFolder);
    const currentDocs = activeFolder ? (byFolder[activeFolder] || []) : [];

    return (
        <div data-testid="portal-documents" className="space-y-6 pb-8">
            <div className="flex items-center gap-3">
                {activeFolder && (
                    <button onClick={() => setActiveFolder(null)} className="w-8 h-8 rounded-lg border flex items-center justify-center hover:bg-zinc-50 transition-colors shrink-0" data-testid="folder-back">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                )}
                <div>
                    <h1 className="text-2xl font-bold leading-tight" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        {activeFolder ? currentFolder?.label : "Documentos"}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {activeFolder ? currentFolder?.description : `${docs.length} documento${docs.length !== 1 ? "s" : ""} en total`}
                    </p>
                </div>
            </div>

            {loading && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 animate-pulse">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-36 rounded-xl bg-zinc-100" />)}
                </div>
            )}

            {!loading && !activeFolder && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-stretch">
                        {FOLDERS.filter((folder) => !folder.hiddenWhenEmpty || (byFolder[folder.key]?.length || 0) > 0).map((folder) => (
                            <FolderCard key={folder.key} folder={folder} count={byFolder[folder.key]?.length || 0} selected={false} onClick={() => setActiveFolder(folder.key)} />
                        ))}
                    </div>
                    {docs.length === 0 && (
                        <Card className="p-10 text-center text-muted-foreground">
                            <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No hay documentos disponibles aún.</p>
                        </Card>
                    )}
                    {docs.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-3">Documentos recientes</p>
                            <div className="space-y-2">
                                {[...docs].sort((a, b) => b.created_at?.localeCompare(a.created_at)).slice(0, 5).map((doc) => {
                                    const folder = FOLDERS.find((f) => f.key === folderOf(doc));
                                    return <DocumentRow key={doc.id} doc={doc} folder={folder} />;
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}

            {!loading && activeFolder && (
                <div>
                    <div className="flex items-center gap-3 rounded-xl px-4 py-3 mb-4" style={{ background: currentFolder?.bg, border: `1.5px solid ${currentFolder?.border}` }}>
                        {currentFolder && <currentFolder.icon className="w-5 h-5 shrink-0" style={{ color: currentFolder.color }} />}
                        <div className="flex-1">
                            <p className="font-bold text-sm" style={{ color: currentFolder?.color }}>{currentFolder?.label}</p>
                            <p className="text-[11px] text-zinc-500">{currentDocs.length} documento{currentDocs.length !== 1 ? "s" : ""}</p>
                        </div>
                    </div>

                    {activeFolder === "certifications" && (
                        <Card className="p-4 mb-4 flex items-center justify-between gap-4 border-2" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }} data-testid="contact-sheet-card">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#FEF3C7" }}>
                                    <Phone className="w-5 h-5" style={{ color: "#D97706" }} />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-bold text-sm text-zinc-800">Hoja de Contactos</p>
                                    <p className="text-[11px] text-zinc-500 leading-snug">
                                        Contactos de Truly Nolen + técnicos de tus últimos servicios
                                        {selectedBranch ? ` · ${selectedBranch.name}` : " (todas las sucursales)"}. Se actualiza cada vez que la generas.
                                    </p>
                                </div>
                            </div>
                            <Button onClick={generateContactSheet} disabled={sheetLoading} className="gap-1.5 shrink-0" style={{ background: "#D97706", color: "#fff" }} data-testid="generate-contact-sheet">
                                {sheetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                {sheetLoading ? "Generando..." : "Generar y descargar"}
                            </Button>
                        </Card>
                    )}
                    {currentDocs.length === 0 ? (
                        <Card className="p-10 text-center text-muted-foreground">
                            <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">Esta carpeta está vacía.</p>
                        </Card>
                    ) : activeFolder === "products" ? (
                        <div className="space-y-2" data-testid="product-accordion-list">
                            {groupProductDocs(currentDocs).map((product) => (
                                <ProductGroupCard
                                    key={product.key}
                                    product={product}
                                    folder={currentFolder}
                                    expanded={expandedProduct === product.key}
                                    onToggle={() => setExpandedProduct(expandedProduct === product.key ? null : product.key)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {currentDocs.map((doc) => <DocumentRow key={doc.id} doc={doc} folder={currentFolder} />)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
